"use client"

import { useState, useEffect } from "react"
import {
  FaArrowDown,
  FaArrowUp,
  FaInfoCircle,
  FaClipboardList,
  FaFileInvoice,
  FaShoppingCart,
  FaHistory,
  FaFileContract,
  FaBuilding,
  FaTruck,
  FaQuestion,
  FaTimes,
  FaHome,
  FaFileDownload,
  FaChevronUp,
  FaChevronDown,
  FaEllipsisV,
  FaDownload,
  FaPhone,
  FaMapMarkerAlt,
  FaRegFileAlt,
} from "react-icons/fa"
import { Link, Outlet, useLocation } from "react-router-dom"
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from "chart.js"
import { db, ref, get, set, onValue, storage } from "../Firebase/firebase-config"
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage"
import { getDatabase, off } from "firebase/database"
import { getAuth } from "firebase/auth"
import { listAll } from "firebase/storage"
import ProfileAlertDialog from "../Firebase/ProfileAlertDialog"
import "./DashBoard.css"
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar } from "recharts"
import { FaArrowTrendUp } from "react-icons/fa6";
// Import the SidebarPull component
import SidebarPull from "./sidebar-pull"
import { IoDocumentSharp } from "react-icons/io5"
import { IndianRupee } from "lucide-react"
import UserBroadcastTicker from "./UserBroadcastTicker"

// Register Chart.js components
ChartJS.register(ArcElement, ChartTooltip, ChartLegend)

const Dashboard = () => {
  // State management
  const [companyName, setCompanyName] = useState(null)
  const [comparison, setComparison] = useState("TodayVsYesterday")
  const [workroundData, setWorkroundData] = useState({})
  const [infoDivData, setInfoDivData] = useState({})
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userUid, setUserUid] = useState("")
  const [userId, setUserId] = useState(null)
  const [ordersData, setOrdersData] = useState([])
  const [stockData, setStockData] = useState({})
  const [selectedBrand, setSelectedBrand] = useState("")

  const [chartSize, setChartSize] = useState({
    height: 200,
    barSize: 30,
    fontSize: 13,
  })
  const [activeTooltip, setActiveTooltip] = useState(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [showDownloads, setShowDownloads] = useState(false)
  const [allSiteAddresses, setAllSiteAddresses] = useState([])

  // Balance data state
  // const [balanceData, setBalanceData] = useState({
  //   totalInitialBalance: 0,
  //   totalCurrentBalance: 0,
  //   totalPayments: 0,
  //   brandBalances: {},
  // })
  const [balanceData, setBalanceData] = useState([]);


  // UI state
  const [showBrandDetails, setShowBrandDetails] = useState(null)
  const [showRefundOptions, setShowRefundOptions] = useState(false)
  const [refundIssue, setRefundIssue] = useState("")
  const [refundFiles, setRefundFiles] = useState([])
  const [selectedBrandForRefund, setSelectedBrandForRefund] = useState("")
  const [bankDetails, setBankDetails] = useState([])
  const [selectedBankDetail, setSelectedBankDetail] = useState(null)
  const [mtcFiles, setMtcFiles] = useState([])
  const [statementFiles, setStatementFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [error, setError] = useState("")
  const [showQueries, setShowQueries] = useState(false)
  const [showMore, setShowMore] = useState(false)

  const auth = getAuth()
  const user = auth.currentUser
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)

  /**
   * Authentication check
   * Sets authentication state and user ID when auth state changes
   */
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsAuthenticated(true)
        setUserUid(user.uid)
      } else {
        setIsAuthenticated(false)
        setUserUid("")
      }
    })

    return () => unsubscribe()
  }, [])

  /**
   * Responsive adjustments
   * Updates chart size based on screen width
   */
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 768) {
        setChartSize({ height: 150, barSize: 20, fontSize: 10 })
      } else {
        setChartSize({ height: 200, barSize: 30, fontSize: 13 })
      }
    }

    updateSize()
    window.addEventListener("resize", updateSize)
    return () => window.removeEventListener("resize", updateSize)
  }, [])

  /**
   * Initialize dashboard data
   * Sets up default comparison data if none exists
   */
  useEffect(() => {
    const initializeDashboardData = async () => {
      if (!userUid) return

      const comparisonOptions = ["TodayVsYesterday", "ThisMonthVsLastMonth", "ThisWeekVsLastWeek", "ThisYearVsLastYear"]

      for (const option of comparisonOptions) {
        const dbRef = ref(db, `userComparisonData/${userUid}/comparisonOptions/${option}`)
        const snapshot = await get(dbRef)

        if (!snapshot.exists()) {
          // Default Data
          const defaultData = {
            workround: { label: "Total Workround", prev: 0, current: 0 },
            infoDivs: {
              costCalculator: { prev: 0, current: 0 },
              supplierTerritories: { prev: 0, current: 0 },
              stockAvailability: { prev: 0, current: 0 },
            },
          }

          await set(dbRef, defaultData)
        }
      }

      fetchDashboardData(userUid, "TodayVsYesterday")
      fetchUserBalances(userUid)
    }

    initializeDashboardData()
  }, [userUid])

  /**
   * Fetches user balance data
   * Retrieves and calculates total and brand-specific balances
   */
  const fetchUserBalances = (userUid) => {
    if (!userUid) return;

    const codesRef = ref(db, `new-code/${userUid}/codes`);

    // Clear previous listener if needed
    off(codesRef);  // ensure only one listener is active

    onValue(codesRef, (snapshot) => {
      const codesData = snapshot.val() || {};
      const allCodeBalances = [];

      Object.entries(codesData).forEach(([codeKey, codeData]) => {
        const brand = codeData.brand || "Unknown";
        const codeID = codeData.codeID || "N/A";
        const balance = codeData.balance || 0;
        const sites = codeData.sites || {};
        const createdAtFormatted = codeData.createdAtFormatted || ""; // Fetch the timestamp

        const siteAddresses = Object.values(sites).map((site) =>
          site?.address ? `${site.address}, ${site.pincode || ""}, ${site.state || ""}` : "N/A"
        );

        allCodeBalances.push({
          brand,
          code: codeKey,
          codeID,
          balance,
          siteAddresses,
          depoAccount: codeData.depoAccount || null,
          createdAtFormatted,
        });
      });

      setBalanceData(allCodeBalances); // ✅ this should trigger UI update
    });
  };

  const parseFormattedDate = (dateString) => {
    if (!dateString) return new Date(0); // Fallback for missing timestamps

    // Example: "06/10/2025, 02:30:44 pm"
    const [datePart, timePart] = dateString.split(", ");
    const [day, month, year] = datePart.split("/").map(Number);
    let [hours, minutes, seconds] = timePart.slice(0, -3).split(":").map(Number);
    const period = timePart.slice(-2).toLowerCase();

    // Convert to 24-hour format
    if (period === "pm" && hours !== 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;

    // Create Date object (months are 0-based in JavaScript)
    return new Date(year, month - 1, day, hours, minutes, seconds);
  };
  /**
   * Fetches dashboard data based on selected comparison period
   */
  const fetchDashboardData = (userUid, selectedComparison) => {
    if (!userUid) return

    const dbRef = ref(db, `userComparisonData/${userUid}/comparisonOptions`)
    const ordersRef = ref(db, `orders/${userUid}`)

    onValue(
      dbRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val()
          let newInfoDivData = {}

          Object.keys(data).forEach((option) => {
            if (option === selectedComparison) {
              newInfoDivData = data[option]?.infoDivs || {}
            }
          })

          onValue(ordersRef, (ordersSnapshot) => {
            if (ordersSnapshot.exists()) {
              const ordersData = ordersSnapshot.val()
              const {
                prevOrders,
                currentOrders,
                prevPayments,
                currentPayments,
                prevTerritories,
                currentTerritories,
                newTerritories,
              } = calculateOrderAndPaymentsComparison(ordersData, selectedComparison)
              // Workround Calculation
              const percentageChange =
                prevOrders === 0 ? (currentOrders > 0 ? 100 : 0) : ((currentOrders - prevOrders) / prevOrders) * 100

              const newWorkroundData = {
                label: `${prevOrders} vs ${currentOrders}`,
                prev: prevOrders,
                current: currentOrders,
                percentage: percentageChange.toFixed(2),
              }

              setWorkroundData(newWorkroundData)

              // Update Cost Calculator with Payment Data
              newInfoDivData.costCalculator = {
                prev: prevPayments,
                current: currentPayments,
              }

              newInfoDivData.supplierTerritories = {
                prev: prevTerritories,
                current: currentTerritories,
                new: newTerritories,
              }

              setInfoDivData(newInfoDivData)
              setOrdersData(ordersData)
            } else {
              setOrdersData([])
            }
          })
        } else {
          console.error("No comparison data found for user:", userUid)
        }
      },
      (error) => {
        console.error("Error fetching data:", error)
      },
    )
  }

  /**
   * Calculates order and payment comparisons between time periods
   */
  const calculateOrderAndPaymentsComparison = (ordersData, comparison) => {
    const now = new Date()
    let prevStart, prevEnd, currentStart, currentEnd

    // Set date ranges based on comparison type
    if (comparison === "TodayVsYesterday") {
      // Today
      currentStart = new Date(now)
      currentStart.setHours(0, 0, 0, 0)
      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Yesterday
      prevStart = new Date(now)
      prevStart.setDate(prevStart.getDate() - 1)
      prevStart.setHours(0, 0, 0, 0)
      prevEnd = new Date(now)
      prevEnd.setDate(prevEnd.getDate() - 1)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisWeekVsLastWeek") {
      // Get the first day of current week (Sunday)
      const dayOfWeek = now.getDay() // 0 for Sunday, 1 for Monday, etc.

      // Current week (Sunday to current day)
      currentStart = new Date(now)
      currentStart.setDate(now.getDate() - dayOfWeek) // Go back to Sunday
      currentStart.setHours(0, 0, 0, 0)

      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Previous week (previous Sunday to previous Saturday)
      prevStart = new Date(currentStart)
      prevStart.setDate(prevStart.getDate() - 7) // Go back 7 days from current week start

      prevEnd = new Date(prevStart)
      prevEnd.setDate(prevStart.getDate() + 6) // Add 6 days to get to Saturday
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisMonthVsLastMonth") {
      // Current month (1st to current date)
      currentStart = new Date(now.getFullYear(), now.getMonth(), 1)
      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Previous month
      const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1
      const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()

      prevStart = new Date(lastMonthYear, lastMonth, 1)

      // Get the same day of month in previous month, or last day if that day doesn't exist
      const currentDay = now.getDate()
      const lastDayOfLastMonth = new Date(lastMonthYear, now.getMonth(), 0).getDate()
      const prevDay = Math.min(currentDay, lastDayOfLastMonth)

      prevEnd = new Date(lastMonthYear, lastMonth, prevDay)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisYearVsLastYear") {
      // Current year (Jan 1 to current date)
      currentStart = new Date(now.getFullYear(), 0, 1)
      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Previous year (same date range in previous year)
      prevStart = new Date(now.getFullYear() - 1, 0, 1)
      prevEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
      prevEnd.setHours(23, 59, 59, 999)
    }

    // Helper function to count orders and payments in a date range
    const getOrderAndPaymentsInRange = (start, end) => {
      let orderCount = 0
      let totalPayment = 0
      const uniqueAddresses = new Set()
      const brandCounts = {}

      if (!ordersData) return { orderCount: 0, totalPayment: 0, uniqueAddresses: new Set(), brandCounts: {} }

      Object.values(ordersData).forEach((order) => {
        if (!order.timestamp) return

        const orderDate = new Date(order.timestamp)

        // Check if the order date is within the range
        if (orderDate >= start && orderDate <= end) {
          orderCount += 1

          // Only add to totalPayment if:
          // 1. It's a new payment (not from existing balance)
          // 2. The paymentFileUrl is not empty (payment proof exists)
          if (order.paymentAmount && !order.isFromBalance) {
            totalPayment += Number.parseFloat(order.paymentAmount) || 0
          }

          // Track unique addresses
          if (order.siteAddress && order.siteAddress.address) {
            uniqueAddresses.add(order.siteAddress.address.toLowerCase())
          }

          // Track brand counts
          if (order.brand) {
            const normalizedBrand = order.brand.toLowerCase()
            brandCounts[normalizedBrand] = (brandCounts[normalizedBrand] || 0) + 1
          }
        }
      })

      return { orderCount, totalPayment, uniqueAddresses, brandCounts }
    }
    const prevData = getOrderAndPaymentsInRange(prevStart, prevEnd)
    const currentData = getOrderAndPaymentsInRange(currentStart, currentEnd)

    // Find New Territories (Addresses in Current that were NOT in Previous)
    const newTerritories = [...currentData.uniqueAddresses].filter((addr) => !prevData.uniqueAddresses.has(addr)).length

    return {
      prevOrders: prevData.orderCount,
      currentOrders: currentData.orderCount,
      prevPayments: prevData.totalPayment,
      currentPayments: currentData.totalPayment,
      prevTerritories: prevData.uniqueAddresses.size,
      currentTerritories: currentData.uniqueAddresses.size,
      newTerritories,
    }
  }

  /**
   * Handles comparison period change
   */
  const handleComparisonChange = (event) => {
    const newComparison = event.target.value
    setComparison(newComparison)
    fetchDashboardData(userUid, newComparison)
  }

  /**
   * Calculates percentage change between two values
   */
  const calculatePercentageChange = (current, prev) => {
    if (prev === 0) return current > 0 ? 100 : 0
    return ((current - prev) / prev) * 100
  }

  /**
   * Renders trend arrow based on percentage change
   */
  const renderArrow = (percentage) => {
    if (percentage > 0) {
      return <FaArrowUp className="trend-icon up" />
    } else if (percentage < 0) {
      return <FaArrowDown className="trend-icon down" />
    } else {
      return <span className="trend-icon neutral"></span>
    }
  }

  /**
   * Toggles tooltip visibility
   */
  const toggleTooltip = (id) => {
    setActiveTooltip(activeTooltip === id ? null : id)
  }

  /**
   * Toggles sidebar visibility
   */
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)

    // Add or remove body class for overlay
    if (!isSidebarOpen) {
      document.body.classList.add("sidebar-open")
    } else {
      document.body.classList.remove("sidebar-open")
    }
  }
  /**
   * Prepares graph data for rendering
   */
  const renderGraphData = () => {
    if (!ordersData || !comparison || Object.keys(ordersData).length === 0) {
      return [
        { name: "Mon", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Tue", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Wed", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Thu", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Fri", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Sat", Pending: 0, Underloading: 0, Dispatch: 0 },
        { name: "Sun", Pending: 0, Underloading: 0, Dispatch: 0 },
      ]
    }

    const now = new Date()
    const currentYear = now.getFullYear()
    const lastYear = currentYear - 1
    const currentMonth = now.getMonth()
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1
    const lastMonthYear = currentMonth === 0 ? lastYear : currentYear
    const statuses = ["Pending", "Underloading", "Dispatch", "Completed"]

    // Set date ranges based on comparison type
    let prevStart, prevEnd, currentStart, currentEnd
    let labels = []

    if (comparison === "TodayVsYesterday") {
      labels = ["Yesterday", "Today"]

      // Today
      currentStart = new Date(now)
      currentStart.setHours(0, 0, 0, 0)
      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Yesterday
      prevStart = new Date(now)
      prevStart.setDate(prevStart.getDate() - 1)
      prevStart.setHours(0, 0, 0, 0)
      prevEnd = new Date(now)
      prevEnd.setDate(prevEnd.getDate() - 1)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisWeekVsLastWeek") {
      labels = getWeekDays()

      // Current week
      const dayOfWeek = now.getDay()
      currentStart = new Date(now)
      currentStart.setDate(now.getDate() - dayOfWeek)
      currentStart.setHours(0, 0, 0, 0)
      currentEnd = new Date(now)
      currentEnd.setHours(23, 59, 59, 999)

      // Previous week
      prevStart = new Date(currentStart)
      prevStart.setDate(prevStart.getDate() - 7)
      prevEnd = new Date(currentStart)
      prevEnd.setDate(prevEnd.getDate() - 1)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisMonthVsLastMonth") {
      labels = [getMonthName(lastMonth), getMonthName(currentMonth)]

      // Current month
      currentStart = new Date(currentYear, currentMonth, 1)
      currentEnd = new Date(now)

      // Previous month
      prevStart = new Date(lastMonthYear, lastMonth, 1)
      prevEnd = new Date(currentYear, currentMonth, 0)
      prevEnd.setHours(23, 59, 59, 999)
    } else if (comparison === "ThisYearVsLastYear") {
      labels = [`${lastYear}`, `${currentYear}`]

      // Current year
      currentStart = new Date(currentYear, 0, 1)
      currentEnd = new Date(now)

      // Previous year
      prevStart = new Date(lastYear, 0, 1)
      prevEnd = new Date(lastYear, 11, 31)
      prevEnd.setHours(23, 59, 59, 999)
    }

    const statusData = {}

    // Initialize data structure
    labels.forEach((label) => {
      statusData[label] = { name: label }
      statuses.forEach((status) => {
        statusData[label][status] = 0
      })
    })

    // Process order data
    Object.values(ordersData).forEach((order) => {
      if (!order.status || !order.timestamp) {
        return
      }

      const orderStatus = order.status.trim()
      if (!statuses.includes(orderStatus)) {
        return
      }

      const orderDate = new Date(order.timestamp)
      let label = ""

      // Determine which label this order belongs to
      if (comparison === "TodayVsYesterday") {
        if (orderDate >= currentStart && orderDate <= currentEnd) {
          label = "Today"
        } else if (orderDate >= prevStart && orderDate <= prevEnd) {
          label = "Yesterday"
        }
      } else if (comparison === "ThisWeekVsLastWeek") {
        if (orderDate >= currentStart && orderDate <= currentEnd) {
          label = orderDate.toLocaleDateString("en-US", { weekday: "short" })
        } else if (orderDate >= prevStart && orderDate <= prevEnd) {
          // For previous week, map to the equivalent day of the week
          const dayOfWeek = orderDate.getDay()
          const currentWeekDay = new Date(currentStart)
          currentWeekDay.setDate(currentWeekDay.getDate() + dayOfWeek)
          label = currentWeekDay.toLocaleDateString("en-US", { weekday: "short" })
        }
      } else if (comparison === "ThisMonthVsLastMonth") {
        if (orderDate.getFullYear() === currentYear && orderDate.getMonth() === currentMonth) {
          label = getMonthName(currentMonth)
        } else if (orderDate.getFullYear() === lastMonthYear && orderDate.getMonth() === lastMonth) {
          label = getMonthName(lastMonth)
        }
      } else if (comparison === "ThisYearVsLastYear") {
        if (orderDate.getFullYear() === currentYear) {
          label = `${currentYear}`
        } else if (orderDate.getFullYear() === lastYear) {
          label = `${lastYear}`
        }
      }

      if (label && statusData[label]) {
        // Increment the count for this status
        statusData[label][orderStatus] = (statusData[label][orderStatus] || 0) + 1

      }
    })

    return Object.values(statusData)
  }

  /**
   * Helper function to get the ISO week number
   */
  const getWeekNumber = (date) => {
    const tempDate = new Date(date.getTime())
    tempDate.setHours(0, 0, 0, 0)
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
    const week1 = new Date(tempDate.getFullYear(), 0, 4)
    return Math.round(((tempDate - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7) + 1
  }

  /**
   * Helper function to get month name
   */
  const getMonthName = (monthIndex) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return monthNames[monthIndex]
  }

  /**
   * Helper function to get the last 7 days (weekdays)
   */
  const getWeekDays = () => {
    const days = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      days.push(d.toLocaleDateString("en-US", { weekday: "short" }))
    }
    return days
  }

  /**
   * Helper function to determine the label for a specific day in weekly comparison
   */
  const getDayLabel = (orderDate) => {
    return orderDate.toLocaleDateString("en-US", { weekday: "short" })
  }

  /**
   * Fetch stock data
   */
  useEffect(() => {
    const db = getDatabase()
    const stockRef = ref(db, "verifyStock")

    onValue(
      stockRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setStockData(snapshot.val())
        } else {
          setStockData({})
        }
      },
      (error) => {
        console.error("Error fetching stock data:", error)
      },
    )
  }, [])

  /**
   * Get stock status color
   */
  const getStockStatusColor = (status) => {
    switch (status) {
      case "In Stock":
        return "var(--success)"
      case "Out of Stock":
        return "var(--danger)"
      case "In Transit":
        return "var(--warning)"
      default:
        return "var(--text-dark)"
    }
  }

  /**
   * Fetch downloadable files
   */
  useEffect(() => {
    if (user) {
      setTimeout(fetchFiles, 500) // Give some time to ensure UID is available
    }
  }, [user])

  /**
   * Fetches MTC and statement files from storage
   */
  const fetchFiles = async () => {
    if (!user) {
      return
    }

    setLoading(true)
    setError("")

    try {
      const mtcRef = storageRef(storage, `mtc/${user.uid}`)
      const mtcList = await listAll(mtcRef)

      const mtcUrls = await Promise.all(
        mtcList.items.map(async (file) => {
          const url = await getDownloadURL(file)
          return { name: file.name, url }
        }),
      )

      const statementRef = storageRef(storage, `user-statements/${user.uid}`)
      let statementUrls = []
      try {
        const statementList = await listAll(statementRef)
        statementUrls = await Promise.all(
          statementList.items.map(async (file) => {
            const url = await getDownloadURL(file)
            return { name: file.name, url }
          }),
        )
      } catch (err) { }

      setMtcFiles(mtcUrls.length > 0 ? mtcUrls.reverse() : [])
      setStatementFiles(statementUrls.length > 0 ? statementUrls.reverse() : [])
    } catch (err) {
      setError(`Error fetching files: ${err.message || "Unknown error"}`)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handles file download
   */
  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      alert("File URL not found. Please try again.")
      return
    }

    // Ensure filename is safe (optional if opening in new tab)
    const safeFileName = fileName.replace(/[<>:"/\\|?*]/g, "_")

    const confirmDownload = window.confirm(`Do you want to open ${safeFileName} in a new tab?`)
    if (confirmDownload) {
      window.open(fileUrl, "_blank") // 👈 opens file in new tab
    }
  }

  /**
   * Info tooltip handlers
   */
  const dashInfo = () => {
    alert(
      "This field allows users to compare performance metrics across different time periods, helping track trends and changes over time.",
    )
  }

  const workOrderInfo = () => {
    alert(
      "This field shows the total work orders compared to the previous period. It highlights the change in order volume and percentage difference.",
    )
  }

  const costInfo = () => {
    alert(
      "This field displays cost comparisons over selected periods, helping track cost changes and manage expenses efficiently.",
    )
  }

  const supplierTerroInfo = () => {
    alert(
      "This field shows the number of supplier territories over time, highlighting changes in your supply network and new territories added.",
    )
  }

  const stockInfo = () => {
    alert(
      "Stock info....\n In Stock: Indicate that the items are in stock and ready for prompt fulfillment. \n Out of Stock: Indicates that the stock is not available and cannot be fulfilled until it is restocked. \n In Transit: Indicates that the stock is currently being transported and will be available soon. \n Note: Stock status may vary over time. Kindly contact our support team for the most accurate information.",
    )
  }

  const balanceInfo = () => {
    alert(
      "This section displays your available balances for different brands. Only brands with a positive balance are shown. Click 'More' to view additional details such as the site address. You can also request a refund for a specific brand if needed.",
    )
  }
  const orderStatusInfo = () => {
    alert(
      "This chart shows the status of orders:\n\n- Pending: Orders waiting for processing\n- Underloading: Orders being prepared for shipment\n- Dispatch: Orders sent out for delivery\n- Completed: Successfully delivered orders",
    )
  }

  /**
   * Check if we're on the dashboard home page
   */
  const isHomePage = () => {
    return location.pathname === "/dashboard" || location.pathname === "/"
  }

  /**
   * Format currency
   */
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // NEW: Fetch bank details for selected brand
  useEffect(() => {
    const fetchBankDetails = async () => {
      if (!selectedBrandForRefund || !userUid) return

      // Find the selected brand data from balanceData
      const selectedBrandData = balanceData.find((item) => `${item.brand}-${item.codeID}` === selectedBrandForRefund)

      if (selectedBrandData && selectedBrandData.depoAccount) {
        setBankDetails([
          {
            balance: selectedBrandData.balance,
            depoAccount: selectedBrandData.depoAccount,
            brand: selectedBrandData.brand,
            codeID: selectedBrandData.codeID,
            siteAddresses: selectedBrandData.siteAddresses,
          },
        ])
      } else {
        setBankDetails([])
      }
    }

    fetchBankDetails()
  }, [selectedBrandForRefund, balanceData, userUid])

  /**
   * Handle refund submission - NEW ENHANCED VERSION
   */
  const gifUrl = "/Images/New notification.gif"

  // Helper functions
  const getIndianTimestamp = () => {
    const now = new Date()
    const istOffset = 5.5 * 60 * 60 * 1000 // IST is UTC+5:30
    const istTime = new Date(now.getTime() + istOffset)
    return istTime.toISOString().replace("T", " ").replace("Z", "")
  }

  const sendNotification = async (userId, refundId, brand, companyName) => {
    const db = getDatabase()
    const notificationRef = ref(db, `supplier_notifications/Refund/${userId}/${refundId}`)

    const newNotification = {
      brand,
      title: "Refund Request",
      message: `A new refund request has been sent by ${companyName}. Please review the refund details.`,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    }

    try {
      await set(notificationRef, newNotification)
    } catch (error) {
      console.error("Error sending notification:", error)
    }
  }

  // NEW: Enhanced refund submission with new-code structure
  const submitRefundRequest = async () => {
    if (!refundIssue.trim() || refundFiles.length === 0 || !userUid || !selectedBrandForRefund) {
      alert("Please fill all required fields")
      return
    }

    setIsSubmitting(true)
    const refundId = new Date().getTime()

    try {
      const fileUrls = {}

      // Upload files
      for (const { file, type } of refundFiles) {
        const folder = type === "letterhead" ? "letterhead" : "cancel_cheque"
        const filePath = `refunds/${userUid}/${folder}/${file.name}`
        const fileRef = storageRef(storage, filePath)

        await uploadBytes(fileRef, file)
        const url = await getDownloadURL(fileRef)
        fileUrls[type] = url
      }

      // Fetch company name
      let companyName = "Unknown Company"
      try {
        const profileRef = ref(db, `profileDashboard/${userUid}`)
        const profileSnap = await get(profileRef)
        if (profileSnap.exists()) {
          const profileData = profileSnap.val()
          if (profileData.company_name) {
            companyName = profileData.company_name
          }
        }
      } catch (e) {
        console.warn("Failed to fetch company name:", e.message)
      }

      // Get selected brand data
      const selectedBrandData = balanceData.find((item) => `${item.brand}-${item.codeID}` === selectedBrandForRefund)

      if (!selectedBrandData) {
        alert("Selected brand data not found")
        setIsSubmitting(false)
        return
      }


      // Also store in original refunds structure for backward compatibility
      const originalRefundsRef = ref(db, `refunds/${userUid}/${refundId}`)
      const originalRefundData = {
        brand: selectedBrandData.brand,
        codeId: selectedBrandData.codeID,
        issue: refundIssue,
        timestamp: getIndianTimestamp(),
        selectedBankDetail: selectedBrandData.depoAccount,
        balance: selectedBrandData.balance,
        // siteAddresses: selectedBrandData.siteAddresses,
        files: fileUrls,
      }

      await set(originalRefundsRef, originalRefundData)

      // Send notification
      await sendNotification(userUid, refundId, selectedBrandData.brand, companyName)

      alert("Refund request submitted successfully!")

      // Reset form
      setRefundIssue("")
      setRefundFiles([])
      setShowRefundOptions(false)
      setSelectedBrandForRefund("")
      setBankDetails([])
      setSelectedBankDetail(null)
    } catch (error) {
      console.error("Refund submission error:", error)
      alert(`Submission failed: ${error.message}`)
    }

    setIsSubmitting(false)
  }

  /**
   * Handle file upload for refund requests
   */
  const handleFileUpload = (event, type) => {
    const maxSize = 5 * 1024 * 1024 // 5MB
    const files = Array.from(event.target.files)

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        alert(`${file.name} exceeds 5MB`)
        return false
      }
      return true
    })

    const typedFiles = validFiles.map((file) => ({ file, type }))
    setRefundFiles((prev) => [...prev, ...typedFiles])
  }

  const handleSidebarClick = (event) => {
    // ✅ Step 1: Check if the clicked element is inside a <Link> tag
    if (event.target.closest("a")) {
      toggleSidebar() // ✅ Step 2: Close the sidebar
    }
  }

  // Add a function to fetch all site addresses from new-code
  const fetchAllSiteAddresses = (userUid) => {
    if (!userUid) return

    const codesRef = ref(db, `new-code/${userUid}/codes`)

    onValue(codesRef, (snapshot) => {
      const codesData = snapshot.val() || {}
      const addresses = []

      Object.values(codesData).forEach((codeData) => {
        const sites = codeData?.sites || {}

        Object.values(sites).forEach((site) => {
          if (site?.address) {
            addresses.push({
              address: site.address,
              pincode: site.pincode || "",
              state: site.state || "",
              fullAddress: `${site.address}, ${site.pincode || ""}, ${site.state || ""}`.trim(),
            })
          }
        })
      })

      setAllSiteAddresses(addresses)
    })
  }

  // Call the function when userUid changes
  useEffect(() => {
    if (userUid) {
      fetchAllSiteAddresses(userUid)
    }
  }, [userUid])

  return (
    <div className="dashboard-main-container">

      {/* Mobile Menu Toggle */}
      <button className="dashboard-mobile-menu-toggle" onClick={toggleSidebar}>
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar Pull for mobile */}
      <SidebarPull onClick={toggleSidebar} isSidebarOpen={isSidebarOpen} />

      <div className="dashboard-container">
        {/* Left Sidebar */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? "open" : ""}`}>
          <nav className="sidebar-nav" onClick={handleSidebarClick}>
            <div className="nav-group">
              <ul>
                <li>
                  <Link
                    to="/dashboard"
                    className={`dashboard-link ${location.pathname === "/dashboard" ? "active" : ""}`}
                  >
                    <FaHome className="dash-icon" />
                    <span>Home</span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className="nav-group">
              <h3>Orders</h3>
              <ul>
                <li>
                  <Link
                    to="/dashboard/product-order"
                    className={`dashboard-link ${location.pathname === "/dashboard/product-order" ? "active" : ""}`}
                  >
                    <FaShoppingCart className="dash-icon" />
                    <span>New Order</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/PO"
                    className={`dashboard-link ${location.pathname === "/dashboard/PO" ? "active" : ""}`}
                  >
                    <FaRegFileAlt className="dash-icon" />
                    <span>Purchase Orders</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/order-status"
                    className={`dashboard-link ${location.pathname === "/dashboard/order-status" ? "active" : ""}`}
                  >
                    <FaClipboardList className="dash-icon" />
                    <span>Order Status</span>
                  </Link>
                </li>
                 <li>
                  <Link
                    to="/dashboard/stock-status"
                    className={`dashboard-link ${location.pathname === "/dashboard/stock-status" ? "active" : ""}`}
                  >
                    <FaArrowTrendUp className="dash-icon" />
                    <span>Stock Status</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/documents"
                    className={`dashboard-link ${location.pathname === "/dashboard/documents" ? "active" : ""}`}
                  >
                    <IoDocumentSharp className="dash-icon" />
                    <span>New Code Docs</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard/previous-orders"
                    className={`dashboard-link ${location.pathname === "/dashboard/previous-orders" ? "active" : ""}`}
                  >
                    <FaHistory className="dash-icon" />
                    <span>Order History</span>
                  </Link>
                </li>
              </ul>
            </div>

            {/* Queries Dropdown */}
            <div className="nav-group">
              <h3 onClick={() => setShowQueries(!showQueries)} style={{ cursor: "pointer" }}>
                Queries {showQueries ? <FaChevronUp /> : <FaChevronDown />}
              </h3>
              {showQueries && (
                <ul>
                  <li>
                    <Link
                      to="/dashboard/new-query"
                      className={`dashboard-link ${location.pathname === "/dashboard/new-query" ? "active" : ""}`}
                    >
                      <FaQuestion className="dash-icon" />
                      <span>Make Query</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/quote-request"
                      className={`dashboard-link ${location.pathname === "/dashboard/quote-request" ? "active" : ""}`}
                    >
                      <FaFileContract className="dash-icon" />
                      <span>Quote Request</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/rate-history"
                      className={`dashboard-link ${location.pathname === "/dashboard/rate-history" ? "active" : ""}`}
                    >
                      <IndianRupee className="dash-icon" />
                      <span>Supplier Rates</span>
                    </Link>
                  </li>
                </ul>
              )}
            </div>

            {/* More Dropdown */}
            <div className="nav-group">
              <h3 onClick={() => setShowMore(!showMore)} style={{ cursor: "pointer" }}>
                More {showMore ? <FaChevronUp /> : <FaChevronDown />}
              </h3>
              {showMore && (
                <ul>
                  <li>
                    <Link
                      to="/dashboard/finance"
                      className={`dashboard-link ${location.pathname === "/dashboard/finance" ? "active" : ""}`}
                    >
                      <FaFileInvoice className="dash-icon" />
                      <span>Finance</span>
                    </Link>
                  </li>

                  {/* Downloads Dropdown */}
                  <li>
                    <div
                      className={`dashboard-link ${showDownloads ? "active" : ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowDownloads(!showDownloads)}
                    >
                      <FaFileDownload className="dash-icon" />
                      <span>Downloads</span>
                      {showDownloads ? (
                        <FaChevronUp className="dropdown-icon" />
                      ) : (
                        <FaChevronDown className="dropdown-icon" />
                      )}
                    </div>
                    {showDownloads && (
                      <ul className="mydropdown-list">
                        <li className="mydropdown-item" onClick={() => setSelectedCategory("mtc")}>
                          <FaFileInvoice className="dash-icon" /> MTC
                        </li>
                        <li className="mydropdown-item" onClick={() => setSelectedCategory("statement")}>
                          <FaFileInvoice className="dash-icon" /> My Statement
                        </li>
                      </ul>
                    )}
                  </li>
                  <li>
                    <Link
                      to="/dashboard/transport-goods"
                      className={`dashboard-link ${location.pathname === "/dashboard/transport-goods" ? "active" : ""}`}
                    >
                      <FaTruck className="dash-icon" />
                      <span>Transport</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/newSupplier-products"
                      className={`dashboard-link ${location.pathname === "/dashboard/newSupplier-products" ? "active" : ""}`}
                    >
                      <FaBuilding className="dash-icon" />
                      <span>New Product Offers</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/dashboard/contact-us"
                      className={`dashboard-link ${location.pathname === "/dashboard/contact-us" ? "active" : ""}`}
                    >
                      <FaPhone className="dash-icon" />
                      <span>Contact Us</span>
                    </Link>
                  </li>
                </ul>
              )}
              {/* Fixed Bar at the Bottom */}
              <div className="bottom-bar">
                <h1>Altrange</h1>
                <h2>Simply Filling needs</h2>
              </div>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          {isHomePage() && <UserBroadcastTicker />}
          {/* Only show the comparison selector on the home page */}
          {isHomePage() && (
            <div className="page-header">
              <div className="comparison-selector">
                <select onChange={handleComparisonChange} value={comparison}>
                  <option value="TodayVsYesterday">Today vs Yesterday</option>
                  <option value="ThisMonthVsLastMonth">This Month vs Last Month</option>
                  <option value="ThisWeekVsLastWeek">This Week vs Last Week</option>
                  <option value="ThisYearVsLastYear">This Year vs Last Year</option>
                </select>
                <FaInfoCircle onClick={dashInfo} className="dashInfoIcon" />
              </div>
            </div>
          )}

          {/* Check if we're on the home route and show dashboard content, otherwise show the Outlet */}
          {isHomePage() ? (
            <>
              {/* Available Balance Cards */}
              {balanceData.length > 0 && (
                <div className="balance-summary">
                  <div className="balance-card">
                    <div className="balance-card-header">
                      <h4>
                        Available Balances{" "}
                        <FaInfoCircle
                          onClick={balanceInfo}
                          style={{ color: "#ea8716", cursor: "pointer", fontSize: "15px" }}
                        />
                      </h4>
                      {activeTooltip === "balanceSummary" && (
                        <div className="tooltip">
                          This shows your available balances for each code under different brands.
                        </div>
                      )}
                    </div>

                    <div className="brand-balances-grid">
                      {/* {balanceData.map(({ brand, code, codeID, balance, siteAddresses }, index) => ( */}
                      {balanceData
                        .slice() // Create a shallow copy to avoid mutating the original array
                        .sort((a, b) => parseFormattedDate(b.createdAtFormatted) - parseFormattedDate(a.createdAtFormatted)) // Sort by timestamp
                        .map(({ brand, code, codeID, balance, siteAddresses, createdAtFormatted }, index) => (
                          <div className="brand-balance-card" key={`${brand}-${code}-${index}`}>
                            {/* Header with brand name and codeID */}
                            <div className="brand-balance-header">
                              <div>
                                <h4>{brand}</h4>
                                <div className="code-id">
                                  <span className="style-code"> Code ID: <strong>{codeID}</strong></span>
                                </div>
                              </div>
                              <div className="dropdown">
                                <button className="dropdown-toggle">
                                  <FaEllipsisV />
                                </button>
                                <div className="dropdown-menu">
                                  <button
                                    className="dropdown-item"
                                    onClick={() => {
                                      setSelectedBrandForRefund(`${brand}-${codeID}`)
                                      setShowRefundOptions(true)
                                    }}
                                  >
                                    Request Refund
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Main content showing balance */}


                            {/* Expandable site details */}
                            {showBrandDetails === `${brand}-${codeID}` && (
                              <>
                                <div className="brand-balance-content">
                                  <h5 className="text-danger fw-bold">Available Balance</h5>
                                  <div className="balance-amount">{formatCurrency(balance)}</div>
                                </div>
                                <div className="brand-details">


                                  <h5 className="site-title">Sites:</h5>
                                  <div className="site-address-scroll-container">
                                    {siteAddresses.length > 0 ? (
                                      siteAddresses.map((site, idx) => (
                                        <div className="site-address-card" key={idx}>
                                          <FaMapMarkerAlt className="site-icon" />
                                          <div className="site-info">
                                            <span className="site-text">{site}</span>
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <p className="no-sites">No site addresses available.</p>
                                    )}
                                  </div>
                                  <button className="close-details" onClick={() => setShowBrandDetails(null)}>
                                    <FaTimes />
                                  </button>
                                </div>
                              </>
                            )}

                            {/* Toggle More button */}
                            {showBrandDetails !== `${brand}-${codeID}` && (
                              <div className="more-button-wrapper">
                                <button className="more-button" onClick={() => setShowBrandDetails(`${brand}-${codeID}`)}>
                                  More <FaChevronDown />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Cards Row */}
              <div className="stats-row">
                <div className="stat-card work-orders">
                  <div className="stat-header">
                    <h3>Work Orders</h3>
                    <button className="info-button" onClick={() => toggleTooltip("workOrders")}>
                      <FaInfoCircle onClick={workOrderInfo} />
                    </button>
                    {activeTooltip === "workOrders" && (
                      <div className="tooltip">This shows the total work orders compared to the previous period.</div>
                    )}
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{workroundData?.current || 0}</div>
                    <div className="stat-comparison">
                      <div className="trend">
                        {renderArrow(calculatePercentageChange(workroundData?.current || 0, workroundData?.prev || 0))}
                        <span className="percentage">{Math.abs(workroundData?.percentage || 0).toFixed(1)}%</span>
                      </div>
                      <div className="prev-value">Previous: {workroundData?.prev || 0}</div>
                    </div>
                  </div>
                </div>

                <div className="stat-card cost-calculator">
                  <div className="stat-header">
                    <h3>Cost Calculator</h3>
                    <button className="info-button" onClick={() => toggleTooltip("costCalculator")}>
                      <FaInfoCircle onClick={costInfo} />
                    </button>
                    {activeTooltip === "costCalculator" && (
                      <div className="tooltip">This displays cost comparisons over selected periods.</div>
                    )}
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">₹{(infoDivData?.costCalculator?.current || 0).toLocaleString()}</div>
                    <div className="stat-comparison">
                      <div className="trend">
                        {renderArrow(
                          calculatePercentageChange(
                            infoDivData?.costCalculator?.current || 0,
                            infoDivData?.costCalculator?.prev || 0,
                          ),
                        )}
                        <span className="percentage">
                          {Math.abs(
                            calculatePercentageChange(
                              infoDivData?.costCalculator?.current || 0,
                              infoDivData?.costCalculator?.prev || 0,
                            ).toFixed(1),
                          )}
                          %
                        </span>
                      </div>
                      <div className="prev-value">
                        Previous: <b className="text-danger">₹</b>{" "}
                        {(infoDivData?.costCalculator?.prev || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="stat-card territories">
                  <div className="stat-header">
                    <h3>My Sites</h3>
                    <button className="info-button" onClick={() => toggleTooltip("territories")}>
                      <FaInfoCircle onClick={supplierTerroInfo} />
                    </button>
                    {activeTooltip === "territories" && (
                      <div className="tooltip">This shows the number of your sites.</div>
                    )}
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">{infoDivData?.supplierTerritories?.current || 0}</div>
                    <div className="stat-comparison">
                      <div className="territory-stats">
                        <div className="territory-stat">
                          <span className="label">Previous:</span>
                          <span className="value">{infoDivData?.supplierTerritories?.prev || 0}</span>
                        </div>
                        <div className="territory-stat">
                          <span className="label">New:</span>
                          <span className="value new">{infoDivData?.supplierTerritories?.new || 0}</span>
                        </div>
                      </div>
                    </div>

                    {/* Site Addresses Section */}
                    <div className="site-addresses-container">
                      <h5 className="site-addresses-title">Site Addresses:</h5>
                      <div className="site-addresses-scroll">
                        {allSiteAddresses.length > 0 ? (
                          allSiteAddresses.map((site, index) => (
                            <div className="site-address-bar" key={index}>
                              <FaMapMarkerAlt className="site-icon" />
                              <span className="site-address-text">{site.fullAddress}</span>
                            </div>
                          ))
                        ) : (
                          <div className="site-address-bar empty">
                            <span>No site addresses available</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashboard-row">
                {/* Order Status Chart */}
                <div className="chart-card bar-chart">
                  <button
                    className="info-button"
                    onClick={() => toggleTooltip("orderStatusInfo")}
                    style={{ float: "right" }}
                  >
                    <FaInfoCircle onClick={orderStatusInfo} />
                  </button>
                  <h3>Order Status</h3>
                  <div className="chart-container">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={renderGraphData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                        <XAxis dataKey="name" tick={{ fill: "var(--text-dark)" }} />
                        {/* <YAxis tick={{ fill: "var(--text-dark)" }} /> */}
                        <YAxis
                          tick={{ fill: "var(--text-dark)" }}
                          allowDecimals={false} // ✅ Force whole numbers
                          domain={[0, 'dataMax + 1']} // Optional: adds a little headroom
                        />

                        <Tooltip
                          contentStyle={{
                            backgroundColor: "var(--card-bg)",
                            border: "1px solid var(--border-color)",
                            borderRadius: "8px",
                          }}
                        />
                        <Legend />
                        <Bar dataKey="Pending" fill="var(--danger)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Underloading" fill="var(--warning)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Dispatch" fill="var(--success)" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Completed" fill="var(--info)" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Stock Availability Card */}
                <div className="card stock-availability">
                  <div className="card-header">
                    <h3>Stock Availability</h3>
                    <button className="info-button" onClick={() => toggleTooltip("stockAvailability")}>
                      <FaInfoCircle onClick={stockInfo} />
                    </button>
                    {activeTooltip === "stockAvailability" && (
                      <div className="tooltip">This shows the current stock status for different cement brands.</div>
                    )}
                  </div>
                  <div className="card-content">
                    <select
                      className="brand-select"
                      value={selectedBrand}
                      onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                      <option value="">Select a brand</option>
                      {Object.keys(stockData).map((brand) => (
                        <option key={brand} value={brand}>
                          {brand}
                        </option>
                      ))}
                    </select>

                    {selectedBrand && stockData[selectedBrand] ? (
                      <div className="stock-list">
                        {Object.entries(stockData[selectedBrand]).map(([id, details]) => (
                          <div key={id} className="stock-item">
                            <div className="stock-details">
                              <div className="stock-detail">
                                <span className="label">Depot/Plant:</span>
                                <span className="value">{details.depot || "N/A"}</span>
                              </div>
                              <div className="stock-detail">
                                <span className="label">Area covered:</span>
                                <span className="value">{details.areaCovered || "N/A"}</span>
                              </div>
                              <div className="stock-detail">
                                <span className="label">Grade:</span>
                                <span className="value">{details.grade || "N/A"}</span>
                              </div>
                              <div className="stock-detail">
                                <span className="label">Status:</span>
                                <span
                                  className="value status"
                                  style={{ color: getStockStatusColor(details.stockStatus) }}
                                >
                                  {details.stockStatus || "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>

                        ))}
                      </div>
                    ) : (
                      <div className="no-data">Select a brand to view stock data</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {/* File Popup */}
      {selectedCategory && (
        <>
          {/* Full-Screen Overlay for Glass Effect */}
          <div className="file-popup-overlay" onClick={() => setSelectedCategory(null)}></div>

          <div className="file-popup">
            <h3>{selectedCategory === "mtc" ? "MTC Files" : "My Statements"}</h3>

            {loading ? (
              <p>Loading files...</p>
            ) : error ? (
              <p className="error">{error}</p>
            ) : (selectedCategory === "mtc" ? mtcFiles.length === 0 : statementFiles.length === 0) ? (
              <p>No files available.</p>
            ) : (
              <ul>
                {(selectedCategory === "mtc" ? mtcFiles : statementFiles).map((file, index) => (
                  <li key={index}>
                    <span>{file.name}</span>
                    <FaDownload className="download-icon" onClick={() => handleDownload(file.url, file.name)} />
                  </li>
                ))}
              </ul>
            )}
            <button onClick={() => setSelectedCategory(null)}>Close</button>
          </div>
        </>
      )}

      {/* Refund Request Modal */}
      {showRefundOptions && (
        <div className="refund-modal-overlay">
          <div className="refund-modal">
            <div className="refund-modal-header">
              <h3>Request Refund for {selectedBrandForRefund}</h3>
              <button
                className="close-modal"
                onClick={() => {
                  setShowRefundOptions(false)
                  setRefundFiles([])
                  setRefundIssue("")
                  setSelectedBrandForRefund("")
                  setBankDetails([])
                  setSelectedBankDetail(null)
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="refund-modal-content">
              <div className="form-group">
                <label htmlFor="refundIssue">Describe your issue:</label>
                <textarea
                  id="refundIssue"
                  value={refundIssue}
                  onChange={(e) => setRefundIssue(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows={4}
                  maxLength={200}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Upload Documents:</label>
                <div className="file-upload-container">
                  <label>
                    Letterhead: <span style={{ fontSize: "12px", color: "#666" }}>(Max file size: 5MB)</span>
                  </label>
                  <input type="file" onChange={(e) => handleFileUpload(e, "letterhead")} className="file-upload" />

                  <label>
                    Cancel Cheque: <span style={{ fontSize: "12px", color: "#666" }}>(Max file size: 5MB)</span>
                  </label>
                  <input type="file" onChange={(e) => handleFileUpload(e, "cancel_cheque")} className="file-upload" />
                </div>


              </div>

              {bankDetails.length > 0 && (
                <div className="form-group">
                  <label>Bank Account Details:</label>
                  <div className="bank-detail-list">
                    {bankDetails.map((detail, index) => (
                      <div key={index} className="bank-detail-option">
                        <div className="bank-info">
                          <p>
                            <strong>Brand:</strong> {detail.brand}
                          </p>
                          <p>
                            <strong>Code ID:</strong> {detail.codeID}
                          </p>
                          <p>
                            <strong>Balance:</strong> ₹{detail.balance}
                          </p>
                          {detail.depoAccount && (
                            <>
                              <p>
                                <strong>Beneficiary Name:</strong> {detail.depoAccount.BeneficiaryName || "N/A"}
                              </p>
                              <p>
                                <strong>Account Number:</strong> {detail.depoAccount.AccountNumber || "N/A"}
                              </p>
                              <p>
                                <strong>Branch:</strong> {detail.depoAccount.Branch || "N/A"}
                              </p>
                              <p>
                                <strong>IFSC:</strong> {detail.depoAccount.IFSC || "N/A"}
                              </p>
                            </>
                          )}

                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="refund-modal-footer">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowRefundOptions(false)
                  setRefundFiles([])
                  setRefundIssue("")
                  setSelectedBrandForRefund("")
                  setBankDetails([])
                  setSelectedBankDetail(null)
                }}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={submitRefundRequest}
                disabled={isSubmitting || !refundIssue.trim() || refundFiles.length === 0}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" /> Submitting...
                  </>
                ) : (
                  "Submit Refund Request"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile overlay */}
      {isSidebarOpen && <div className="mobile-overlay" onClick={toggleSidebar}></div>}
    </div>
  )
}

export default Dashboard
